import type {
  ImportantContactAddress,
  ImportantContactBodyDtos
} from '@lmc/ts-common'
import {
  FormCard,
  FormItem,
  FormRadio,
  FormRadioGroup,
  Input,
  TextArea
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Form } from 'antd'
import { EMAIL_MAX_LENGTH, TELEPHONE_MAX_LENGTH } from 'common/constants'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import { AddressForm } from 'screens/Residents/screens/ResidentProfileScreen/screens/ResidentContactDetails/FormCards/Address/AddressForm'

export type ContactDetailsFormType = {
  address: Pick<
    ImportantContactAddress,
    'street1' | 'street2' | 'state' | 'postcode' | 'name' | 'suburb'
  >
} & Pick<
  ImportantContactBodyDtos,
  'telephone' | 'telephoneSecondary' | 'contactStatus' | 'contactableTimes'
>

export enum ContactStatus {
  priority = 'priority',
  secondary = 'secondary',
  doNotContact = 'doNotContact'
}

const ContactDetailsFormCard = ({ testId }: { testId?: string } = {}) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  return (
    <FormCard
      title={t(supConstants.CONTACT_DETAILS)}
      data-testid={
        testId
          ? `${testId}-contact-details-form-card`
          : 'contact-details-form-card'
      }
    >
      <Form.Item shouldUpdate={() => false}>
        {({ getFieldValue, setFieldValue }) => {
          const isAddressFilled = !!getFieldValue(['address'])
          return (
            <AddressForm
              fieldName={['address']}
              onAutofill={(values) => setFieldValue('address', values)}
              title={t(supConstants.ADDRESS)}
              initialIsAddingAddressManually={isAddressFilled}
              data-testid={
                testId
                  ? `${testId}-contact-address-form`
                  : 'contact-address-form'
              }
            >
              <FormItem
                label={<Text>{t(supConstants.BUILDING_NAME)}</Text>}
                name={['address', 'name']}
              >
                <Input
                  data-testid={
                    testId
                      ? `${testId}-contact-address-building-name`
                      : 'contact-address-building-name'
                  }
                />
              </FormItem>
              <FormItem
                label={<Text>{t(supConstants.ADDRESS_LINE_1)}</Text>}
                name={['address', 'street1']}
              >
                <Input
                  data-testid={
                    testId
                      ? `${testId}-contact-address-line-1`
                      : 'contact-address-line-1'
                  }
                />
              </FormItem>
              <FormItem
                label={<Text>{t(supConstants.ADDRESS_LINE_2)}</Text>}
                name={['address', 'street2']}
              >
                <Input
                  data-testid={
                    testId
                      ? `${testId}-contact-address-line-2`
                      : 'contact-address-line-2'
                  }
                />
              </FormItem>
              <FormItem
                label={<Text>{t(supConstants.TOWN_CITY)}</Text>}
                name={['address', 'suburb']}
              >
                <Input
                  data-testid={
                    testId
                      ? `${testId}-contact-address-town-city`
                      : 'contact-address-town-city'
                  }
                />
              </FormItem>
              <FormItem
                label={<Text>{t(supConstants.COUNTRY)}</Text>}
                name={['address', 'state']}
              >
                <Input
                  data-testid={
                    testId
                      ? `${testId}-contact-address-country`
                      : 'contact-address-country'
                  }
                />
              </FormItem>
            </AddressForm>
          )
        }}
      </Form.Item>
      <FormItem label={t(supConstants.TELEPHONE1)} name="telephone">
        <Input
          maxLength={TELEPHONE_MAX_LENGTH}
          data-testid={
            testId ? `${testId}-contact-telephone1` : 'contact-telephone1'
          }
        />
      </FormItem>
      <FormItem label={t(supConstants.TELEPHONE2)} name="telephoneSecondary">
        <Input
          maxLength={TELEPHONE_MAX_LENGTH}
          data-testid={
            testId ? `${testId}-contact-telephone2` : 'contact-telephone2'
          }
        />
      </FormItem>
      <FormItem label={t(supConstants.EMAIL)} name="email">
        <Input
          type="email"
          name="email"
          maxLength={EMAIL_MAX_LENGTH}
          data-testid={testId ? `${testId}-contact-email` : 'contact-email'}
        />
      </FormItem>
      <FormItem label={t(supConstants.CONTACT_STATUS)} name="contactStatus">
        <FormRadioGroup
          data-testid={
            testId ? `${testId}-contact-status-group` : 'contact-status-group'
          }
        >
          {Object.keys(ContactStatus).map((status) => (
            <FormRadio
              key={status}
              value={status}
              text={t(supConstants[status as ContactStatus])}
              data-testid={
                testId
                  ? `${testId}-contact-status-${status}`
                  : `contact-status-${status}`
              }
            />
          ))}
        </FormRadioGroup>
      </FormItem>
      <FormItem
        label={t(supConstants.CONTACTABLE_TIMES)}
        name="contactableTimes"
      >
        <TextArea
          data-testid={
            testId
              ? `${testId}-contact-contactable-times`
              : 'contact-contactable-times'
          }
        />
      </FormItem>
    </FormCard>
  )
}

export default ContactDetailsFormCard
